<template>
    <v-col align-self="auto" cols="12" xl="3" lg="5" md="6" sm="8">
        <div class="d-block text-center my-6 py-4">
            <img :src="getLogo()" height="60"/>
        </div>

        <v-card elevation="2" class="pa-6 dddark" :loading="loading" :disabled="loading">
            <div class="text-center pt-6 mt-6 mb-3">
                <span class="d-block text-h4 py-2">{{ $t('components.ResetPasswordForm.FormTitle') }}</span>
                <span class="d-block text-subtitle-1">{{ $t('components.ResetPasswordForm.FormSubtitle') }}</span>
            </div>

            <v-card-text v-if="error">
                <v-alert dense outlined type="error">{{ error }}</v-alert>
            </v-card-text>

            <v-card-text class="pa-6">
                <v-form ref="form" id="form" v-model="valid" @submit.prevent="resetPassword">
                    <v-stepper v-model="step" class="elevation-0 dddark">
                        <v-stepper-items>
                            <v-stepper-content step="1" class="px-0">
                                <p class="mb-4">{{ $t('components.ResetPasswordForm.EmailText') }}</p>
                                <v-text-field 
                                    v-model="email" 
                                    solo light
                                    class="dd-input"
                                    :label="$t('components.ResetPasswordForm.EmailLabel')" 
                                    :rules="[rules.required, rules.email]"
                                    hint="example@domain.com"/>
                            </v-stepper-content>

                            <v-stepper-content step="2" class="px-0">
                                <p class="mb-4">{{ $t('components.ResetPasswordForm.CodeText') }}</p>
                                <v-text-field 
                                    solo light
                                    class="dd-input"
                                    v-model="code"
                                    :counter="6"
                                    :label="$t('components.ResetPasswordForm.CodeLabel')" 
                                    :rules="[rules.required, rules.code]"
                                    hint="XXXXXX"/>

                                <v-text-field 
                                    solo light
                                    class="dd-input"
                                    v-model="password"
                                    :type="showPass ? 'text' : 'password'"
                                    :label="$t('components.LoginForm.PasswordLabel')" 
                                    :rules="[rules.required, passwordRule]"
                                    :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                                    @click:append="showPass = !showPass"/>
                            </v-stepper-content>
                        </v-stepper-items>
                    </v-stepper>

                    <v-btn
                        v-if="step == 1"
                        block 
                        color="success" 
                        elevation="1" 
                        class="text-h6"
                        style="text-transform: none" 
                        height="56px"
                        @click="sendCode" 
                        :disabled="!rules.validEmail(email)">
                        {{ $t('components.ResetPasswordForm.SendCodeButton') }}
                    </v-btn>
                
                    <v-btn
                        v-if="step == 2"
                        block 
                        type="submit" 
                        form="form" 
                        color="success" 
                        elevation="1" 
                        class="text-h6"
                        style="text-transform: none" 
                        height="56px"
                        :disabled="!valid">
                        {{ $t('components.ResetPasswordForm.ResetPasswordButton') }}
                    </v-btn>
                </v-form>

            </v-card-text>
            <v-card-actions class="py-6 text-center d-block text-body-2">
                <router-link class="text-decoration-none" :to="{ name: 'login' }">
                    {{ $t('components.ResetPasswordForm.LoginButton') }}
                </router-link>
                <p>Questions? Contact our <a href="mailto:reporting@getdeardoc.com">support team</a>.</p>
            </v-card-actions>
        </v-card>
    </v-col>
</template>

<script>
import AuthAPI from '@/utils/AuthAPI';
import EventBus from '@/utils/EventBus';

/**
 * ResetPasswordForm component includes the required fields and functions to 
 * perform a recovery password process. It uses the AuthAPI class to wrap AWS 
 * Cognito service.
 */
export default {
    name: 'ResetPasswordForm',
    data: () => ({
        valid: true,
        loading: false,
        step: 1,
        email: '',
        code: '',
        password: '',
        showPass: false,
        error: '',
        rules: {
            required: value => !!value || 'Required.',
            code: value => !isNaN(value) && value.length === 6 || 'Invalid code. Ex.: XXXXXX.',
            email: value => {
                const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                return pattern.test(value) || 'Invalid e-mail.'
            },
            validEmail: value => {
                const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                return pattern.test(value);
            }
        }
    }),
    methods: {
        getLogo() {
            const { isDark } = this.$vuetify.theme;
            return isDark ? '/logow.svg' : '/logo.svg';
        },
        passwordRule(value) {
            if (!value || value.length < 8) return this.$t('components.ResetPasswordForm.PasswordFullRequirements');
            else if (!value.match('[a-z]+')) return this.$t('components.ResetPasswordForm.PasswordLowerCaseRequirements');
            else if (!value.match('[A-Z]+')) return this.$t('components.ResetPasswordForm.PasswordUpperCaseRequirements');
            else if (!value.match('[0-9\\W]+')) return this.$t('components.ResetPasswordForm.PasswordNumberSymbolRequirements');
            return true;
        },
        async sendCode() {
            const { email } = this;
            this.toggleLoading();

            const [ success, error ] = await AuthAPI.sendRecoveryPasswordCode(email);
            if (!success) this.error = error.message;
            else this.step = 2;

            this.toggleLoading();

        },
        async resetPassword() {
            const { email, code, password } = this;
            this.toggleLoading();

            const [ success, error ] = await AuthAPI.recoveryPassword(email, code, password);

            if (!success) this.error = error.message;
            else {
                EventBus.$emit("popup", "Password saved successfully!");
                this.$router.push({ name: 'login' });
            }

            this.toggleLoading(true);
        },
        toggleLoading(clear = false) {
            if (this.loading) {
                this.loading = false;
                if (clear) this.$refs.form.reset();
            } else this.loading = true;
        }
    }
}
</script>